'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { capturePageView, initSourceTracker } from '@/lib/source-tracker';

/**
 * Инициализирует Source Tracker и захватывает pageview на каждой смене роута.
 *
 * Архитектура для Next.js App Router:
 *   - на маунте: initSourceTracker() → ping /api/log/ping → получение identity → первый pageview
 *   - на смене pathname/searchParams: capturePageView() (НЕ дублирует первый из init)
 *
 * Skip-инициализация для админки (не хотим засорять метрику internal-трафиком).
 */
export function SourceTrackerInit() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initRef = useRef(false);
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Не трекаем визиты в админке — это internal-трафик, испортит аналитику
    if (pathname?.startsWith('/admin')) return;

    if (!initRef.current) {
      initRef.current = true;
      initSourceTracker().catch(() => { /* swallow */ });
      lastUrlRef.current = window.location.href;
      // initSourceTracker сам отправит первый pageview
      return;
    }

    // На последующих сменах роута — отправляем pageview
    const currentUrl = window.location.href;
    if (currentUrl !== lastUrlRef.current) {
      lastUrlRef.current = currentUrl;
      capturePageView();
    }
  }, [pathname, searchParams]);

  return null;
}
